import { fetchApiGateway } from "@nova/fetch-api-gateway"
import {
    CreatableReportTemplate,
    creatableToRenderConfig,
    ReportTemplate,
    templateToRenderConfig,
    UpdatableReportTemplate,
} from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import {
    selectActiveWorkspace,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"
import { downloadBlob } from "tools/browser/download"
const loadReportPdfGenerator = () =>
    import("./pilotTrackingReportPdf").then((m) => m.pilotTrackingReportPdf)

const REPORT_TEMPLATE_KEY = "report-template"

const invalidateReportTemplates = () =>
    mutate((key) => Array.isArray(key) && key[0] === REPORT_TEMPLATE_KEY)

const getReportTemplates = (orgId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/report-template", {
        params: { orgId },
    })
}

export const useReportTemplates = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR([REPORT_TEMPLATE_KEY, orgId], () =>
        orgId ? getReportTemplates(orgId) : undefined
    )

    return { data, isLoading }
}

const getReportTemplate = (orgId: string, reportTemplateId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/report-template/:reportTemplateId", {
        params: { orgId, reportTemplateId },
    })
}

export const useReportTemplate = (reportTemplateId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWRNoRevalidate(
        [REPORT_TEMPLATE_KEY, orgId, reportTemplateId],
        () => (orgId ? getReportTemplate(orgId, reportTemplateId) : undefined)
    )

    return { data, isLoading }
}

const createReportTemplate = (orgId: string, body: Omit<CreatableReportTemplate, "created_by">) => {
    return fetchApiGateway("post /org/:orgId/fleet/report-template", {
        body,
        params: { orgId },
    })
}

export const useCreateReportTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-report-template",
        (_key, { arg }: { arg: Omit<CreatableReportTemplate, "created_by"> }) =>
            orgId ? createReportTemplate(orgId, arg) : undefined,
        { onSuccess: invalidateReportTemplates }
    )

    return { createReportTemplate: trigger, isCreating: isMutating }
}

const updateReportTemplate = (
    orgId: string,
    reportTemplateId: string,
    body: UpdatableReportTemplate
) => {
    return fetchApiGateway("patch /org/:orgId/fleet/report-template/:reportTemplateId", {
        body,
        params: { orgId, reportTemplateId },
    })
}

export const useUpdateReportTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-report-template",
        (_key, { arg }: { arg: { reportTemplateId: string; data: UpdatableReportTemplate } }) =>
            orgId ? updateReportTemplate(orgId, arg.reportTemplateId, arg.data) : undefined,
        { onSuccess: invalidateReportTemplates }
    )

    return { updateReportTemplate: trigger, isUpdating: isMutating }
}

const deleteReportTemplate = (orgId: string, reportTemplateId: string) => {
    return fetchApiGateway("delete /org/:orgId/fleet/report-template/:reportTemplateId", {
        params: { orgId, reportTemplateId },
    })
}

export const useDeleteReportTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-report-template",
        (_key, { arg }: { arg: string }) => (orgId ? deleteReportTemplate(orgId, arg) : undefined),
        { onSuccess: invalidateReportTemplates }
    )

    return { deleteReportTemplate: trigger, isDeleting: isMutating }
}

const fetchReportContext = () => {
    const state = store.getState()
    const members = selectWorkspaceMembers(state)
    const org = selectActiveWorkspace()(state)!
    return { members, org }
}

const generateReport = async (orgId: string, reportTemplateId: string, name: string) => {
    const template = await getReportTemplate(orgId, reportTemplateId)
    if (!template) throw new Error("Report template not found")
    const { members, org } = fetchReportContext()
    const pilotTrackingReportPdf = await loadReportPdfGenerator()
    const pdf = await pilotTrackingReportPdf(templateToRenderConfig(template), members, org)
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
    const filename = `${name || `${template.reportType}_${timestamp}`}.pdf`
    await downloadBlob(filename, new Blob([pdf], { type: "application/pdf" }))
}

export const useGenerateReport = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "generate-report",
        (_key, { arg }: { arg: { id: string; name: string } }) =>
            orgId ? generateReport(orgId, arg.id, arg.name) : undefined
    )

    return { generateReport: trigger, isGenerating: isMutating }
}

const generateReportFromCreatableTemplate = async (
    orgId: string,
    creatable: CreatableReportTemplate
) => {
    const { members, org } = fetchReportContext()
    const pilotTrackingReportPdf = await loadReportPdfGenerator()
    const pdf = await pilotTrackingReportPdf(
        creatableToRenderConfig(orgId, creatable),
        members,
        org
    )
    await downloadBlob(`${creatable.name}.pdf`, new Blob([pdf], { type: "application/pdf" }))
}

export const useGenerateReportFromCreatableTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "generate-report-from-creatable-template",
        (_key, { arg }: { arg: { template: CreatableReportTemplate } }) =>
            orgId ? generateReportFromCreatableTemplate(orgId, arg.template) : undefined
    )

    return { generateReport: trigger, isGenerating: isMutating }
}

export const generateCSVReport = async (orgId: string, reportTemplateId: string, name: string) => {
    const response = await fetchApiGateway(
        "get /org/:orgId/fleet/report-template/:reportTemplateId/csv/generate",
        { params: { orgId, reportTemplateId }, rawResponse: true }
    )
    const disposition = response.headers.get("Content-Disposition")
    const filename = disposition?.match(/filename="(.+)"/)?.[1] ?? `${name}.csv`

    downloadBlob(filename, await response.blob())
}

export const useDuplicateReportTemplate = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "duplicate-report-template",
        (_key, { arg: { template } }: { arg: { template: ReportTemplate } }) => {
            if (!orgId) return undefined
            const body: CreatableReportTemplate = {
                name: `${template.name} - Duplicated`,
                description: template.description,
                report_type: template.reportType,
                date_range: template.dateRange,
                filters: template.filters,
                format_config: template.formatConfig,
                schedule: template.schedule,
                notification_config: template.notificationConfig,
            }
            return createReportTemplate(orgId, body)
        },
        { onSuccess: invalidateReportTemplates }
    )

    return { duplicateReportTemplate: trigger, isDuplicating: isMutating }
}

export const useGenerateReportCSV = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "generate-report-csv",
        (_key, { arg }: { arg: { id: string; name: string } }) =>
            orgId ? generateCSVReport(orgId, arg.id, arg.name) : undefined
    )

    return { generateReportCSV: trigger, isGenerating: isMutating }
}
